import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input, OnChanges,
  OnDestroy,
  OnInit,
  Output, SimpleChanges
} from '@angular/core';

import { BehaviorSubject, Observable, Subscription, timer, take } from 'rxjs';

import { ScreenType } from '../responsive/responsive.component';

const hoursInADay = 24;
const minutesInAnHour = 60;
const secondsInAMinute  = 60;
const zeroPad = (num: number, places: number): string => num.toString().padStart(places, '0');

class Timer {

  constructor(
    public days = '',
    public hours = '',
    public minutes = '',
    public seconds = ''
  ) {
  }
}

export class CountdownTimer {
  public isExpired: boolean;

  constructor(private _time: Date, private readonly _delay: number) {
    this._time = _time;
    this._delay = _delay;
  }

  public expired(): boolean {
    return this.isExpired || this.countdown() <= 0;
  }

  public countdown(): number {
    const current = new Date();
    return this._delay - (current.getTime() - this._time.getTime());
  }
}

@Component({
  selector: 'countdown-timer',
  templateUrl: './countdown-timer.component.html',
  styleUrls: ['./countdown-timer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CountdownTimerComponent implements OnInit, OnDestroy, OnChanges {
  @Input() countdown: CountdownTimer | null;
  @Input() template = 'profile-common.label.countdown';
  @Input() hasCountNames = false;
  @Input() widget = false;
  @Input() showCompletedTimer = false;

  @Input() step = 1000;
  @Input() mode: 'numbers' | '' = '';
  @Input() viewMode: 'x:x' | 'x:x:x' | 'x:x:x:x' = 'x:x';
  @Input() isResponsive = true;

  @Output() complete = new EventEmitter<void>();

  public info: string;
  public screenType = ScreenType;

  public get timer$(): Observable<Timer | string> {
    return this._timer$.asObservable();
  }

  private _timer$ = new BehaviorSubject<Timer | string>('');
  private _countDownSubs: Subscription;

  ngOnInit(): void {
    this.codeStep(this.countdown);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { countdown } = changes;
    if (!countdown) return;
    if (!countdown.firstChange) {
      this.codeStep(countdown.currentValue);
    }
  }

  ngOnDestroy(): void {
    if (this._countDownSubs) {
      this._countDownSubs.unsubscribe();
    }
  }

  public getFormattedTimer(timer: Timer): string {
    return Object.values(timer).filter(char => char).join(':');
  }

  public codeStep(code: CountdownTimer): void {
    let diff = Math.floor(code.countdown() / 1000) + 1;

    this._renderText(diff);

    this._countDownSubs = timer(this.step, this.step)
      .pipe(take(diff))
      .subscribe(() => {
        diff -= 1;

        this._renderText(diff);

        if (diff <= 0) {
          this._countDownSubs.unsubscribe();
          this.complete.emit();
          if (!this.showCompletedTimer) {
            this._timer$.next('');
          }
        }
      });
  }

  private _renderText(diff: number): void {
    const days = Math.floor(diff / (minutesInAnHour * secondsInAMinute * hoursInADay));
    const hours = Math.floor(diff / (minutesInAnHour * secondsInAMinute) % hoursInADay);
    const minutes = Math.floor(diff / minutesInAnHour % secondsInAMinute);
    const seconds = Math.floor(diff % secondsInAMinute);

    const timer = new Timer();

    switch (this.viewMode) {
      case 'x:x':
        if (diff <= 0) {
          this._timer$.next(new Timer('', '', '00', '00'));
          break;
        }
        timer.days = days ? zeroPad(days, 2) : '';
        timer.hours = hours ? zeroPad(hours, 2) : '';
        timer.minutes = !days ? zeroPad(minutes, 2) : '';
        timer.seconds = !days && !hours ? zeroPad(seconds, 2) : '';
        this._timer$.next(timer);
        break;
      case 'x:x:x':
        if (diff <= 0) {
          this._timer$.next(new Timer('', '00', '00', '00'));
          break;
        }
        timer.days = days ? zeroPad(days, 2) : '';
        timer.hours = zeroPad(hours, 2);
        timer.minutes = zeroPad(minutes, 2);
        timer.seconds = !days ? zeroPad(seconds, 2) : '';
        this._timer$.next(timer);
        break;
      case 'x:x:x:x':
        if (diff <= 0) {
          this._timer$.next(new Timer('00', '00', '00', '00'));
          break;
        }
        timer.days = zeroPad(days, 2);
        timer.hours = zeroPad(hours, 2);
        timer.minutes = zeroPad(minutes, 2);
        timer.seconds = zeroPad(seconds, 2);
        this._timer$.next(timer);
        break;
    }
  }
}
